import type { Session } from '../../api.ts';
import { icon } from '../../icons.ts';
import { h } from '../../ui.ts';

/**
 * Shared parts of the phone's screens (design-docs/0028): the screen frame, work cards and counts. Icons live in
 * icons.ts and are re-exported here for the phone views.
 */
export { icon };

export interface WorkItem {
  identifier: string;
  title: string;
  state: 'filing' | 'filed' | 'queued' | 'building' | 'needs-you' | 'ready' | 'failed' | 'done';
  detail: string;
  url: string | null;
  preview: string | null;
}
export type TalkSession = Session & { resumed?: boolean };

export function screen(...children: Array<Node | string | null>) {
  return h('div', { class: 'p-screen' }, ...children);
}

/** A shared view (agents, an agent, projects) on the phone: a back button above it, the page scrolling inside. */
export function phonePage(back: string, view: HTMLElement): HTMLElement {
  return screen(
    h(
      'header',
      { class: 'p-head' },
      h('a', { class: 'p-icon', href: back, 'aria-label': 'Back' }, icon.back()),
      h('span', { class: 'p-brand small' }, h('span', { class: 'brand-mark' }, icon.mark(12)), 'Assistant'),
      h('a', { class: 'p-icon', href: '#/talk', 'aria-label': 'Talk' }, icon.mic()),
    ),
    view,
  );
}

export const remember = (k: string, v?: string) => {
  try {
    if (v === undefined) return localStorage.getItem(k);
    localStorage.setItem(k, v);
  } catch {
    /* private mode: nothing to remember */
  }
  return null;
};

export const counts = (items: WorkItem[]) => ({
  working: items.filter((i) => ['filing', 'filed', 'queued', 'building'].includes(i.state)).length,
  needs: items.filter((i) => i.state === 'needs-you').length,
  ready: items.filter((i) => i.state === 'ready').length,
});

const dotOf = (s: WorkItem['state']) =>
  s === 'needs-you' ? 'tone-warning' : s === 'ready' ? 'tone-success' : s === 'failed' ? 'tone-danger' : 'tone-accent';

export function itemCard(i: WorkItem): HTMLElement {
  const cls = i.state === 'needs-you' ? 'p-card amber' : i.state === 'ready' ? 'p-card ready' : 'p-card';
  const lead =
    i.state === 'ready' ? h('span', { class: 'ok' }, icon.check()) : h('span', { class: `dot ${dotOf(i.state)}` });
  const body = h(
    'span',
    { class: 'p-text' },
    h('span', {}, h('span', { class: 'p-mono' }, i.identifier), '  ', i.title),
    h('span', { class: 'p-muted' }, i.state === 'ready' ? 'Preview ready · tap to open' : i.detail),
  );
  const href = i.preview ?? i.url;
  return href
    ? h('a', { class: cls, href, target: '_blank', rel: 'noopener' }, lead, body, icon.open())
    : h('div', { class: cls }, lead, body);
}
